import React, { useEffect, useState, useRef } from "react";
import { getDatabase, ref, get, set, push } from "firebase/database";
import { getAuth, onAuthStateChanged } from "firebase/auth"; 
import app from "../firebase-config";
import Swal from "sweetalert2";

function POS() {
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [cart, setCart] = useState([]);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [isButtonVisible, setIsButtonVisible] = useState(false);
  const [username, setUsername] = useState("");
  const [selectedSize, setSelectedSize] = useState({});
  const [selectedQuantity, setSelectedQuantity] = useState({}); // Track selected quantity for items
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");

  const cartRef = useRef(null);

  const fetchItems = async () => {
    const db = getDatabase(app);
    const dbRef = ref(db, "beverages/coffee");
    const snapshot = await get(dbRef);
    if (snapshot.exists()) {
      const myData = snapshot.val();
      const itemsArray = Object.keys(myData).map((key) => ({
        ...myData[key],
        itemId: key,
      }));
      setItems(itemsArray);
      
      const uniqueCategories = [
        "All",
        ...new Set(itemsArray.map((item) => item.category || "Uncategorized")),
      ];
      setCategories(uniqueCategories);
      filterItems(searchQuery, selectedCategory, itemsArray);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const filterItems = (query, category, itemList) => {
    let filtered = itemList.filter(
      (item) =>
        item.coffeeName.toLowerCase().includes(query) ||
        item.category.toLowerCase().includes(query)
    );

    if (category !== "All") {
      filtered = filtered.filter((item) => item.category === category);
    }
    setFilteredItems(filtered);
  };

  useEffect(() => {
    filterItems(searchQuery, selectedCategory, items);
  }, [searchQuery, selectedCategory, items]);


  const addToCart = (item) => {
    const quantity = selectedQuantity[item.itemId] || 1;
    const size = selectedSize[item.itemId] || "N/A"; // Default to "N/A" if no size is selected
  
    // Map size labels to actual price keys
    const sizeMapping = {
      S: "small",
      M: "medium",
      L: "large",
      "N/A": "single",
    };
  
    // Determine the correct price
    const priceKey = sizeMapping[size] || "single"; // Default to "single" if not found
    const price = item.prices?.[priceKey] || item.prices?.single || 0;
  
    setCart((prevCart) => {
      const existingItem = prevCart.find(
        (cartItem) => cartItem.itemId === item.itemId && cartItem.size === size
      );
  
      Swal.fire({
        title: "Item added to cart!",
        icon: "success",
        draggable: false,
      });
  
      if (existingItem) {
        return prevCart.map((cartItem) =>
          cartItem.itemId === item.itemId && cartItem.size === size
            ? { ...cartItem, quantity: cartItem.quantity + quantity }
            : cartItem
        );
      } else {
        return [...prevCart, { ...item, quantity, size, price }];
      }
    });
  
    setSelectedQuantity((prev) => ({ ...prev, [item.itemId]: 1 }));
  };
  
  
  const handleQuantityChangeInCart = (event, itemId, size) => {
    const newQuantity = parseInt(event.target.value, 10);
    if (newQuantity < 1) return;
  
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.itemId === itemId && item.size === size
          ? { ...item, quantity: newQuantity }
          : item
      )
    );
  };
  

  const handleSelectedQuantityChange = (itemId, change) => {
    setSelectedQuantity((prev) => {
      const newQuantity = (prev[itemId] || 1) + change;
      return { ...prev, [itemId]: newQuantity < 1 ? 1 : newQuantity };
    });
  };

  const calculateTotal = () => {
    let sum = 0;
    cart.forEach((item) => {
      sum += item.quantity * item.price;
    });
    setTotal(sum);
  };

  useEffect(() => {
    calculateTotal();
  }, [cart]);

  const handleSearch = (event) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);
    const filtered = items.filter((item) =>
      item.coffeeName.toLowerCase().includes(query) ||
      item.category.toLowerCase().includes(query)
    );
    setFilteredItems(filtered);
  };

  const completeOrder = async () => {
    const db = getDatabase(app);
    const user = username;
    const updatedCartItems = [...cart];
  
    // Include size in saleData
    const saleData = {
      user: user,
      items: updatedCartItems.map((item) => ({
        ...item,
        size: selectedSize[item.itemId] || "S", // Default to "S" if size is not selected
      })),
      totalAmount: total,
      timestamp: new Date().toISOString(),
    };
  
    const salesRef = ref(db, "sales");
    await push(salesRef, saleData);
  
    for (let i = 0; i < updatedCartItems.length; i++) {
      const item = updatedCartItems[i];
      const itemRef = ref(db, "beverages/coffee/" + item.itemId);
      const snapshot = await get(itemRef);
  
      if (snapshot.exists()) {
        const itemData = snapshot.val();
        const selectedItemSize = selectedSize[item.itemId] || "S"; // Default to small
  
        // Deduction based on size
        const sizeDeduction = selectedItemSize === "L" ? 2 : selectedItemSize === "M" ? 1.5 : 1;
        const updatedQuantity = Math.max(0, itemData.quantity - sizeDeduction);
  
        await set(itemRef, {
          ...itemData,
          quantity: updatedQuantity,
        });
  
        setItems((prevItems) =>
          prevItems.map((prevItem) =>
            prevItem.itemId === item.itemId ? { ...prevItem, quantity: updatedQuantity } : prevItem
          )
        );
      }
    }
  
    setCart([]);
    setTotal(0);
    fetchItems();
    Swal.fire({
      title: "Order completed successfully!",
      icon: "success",
      draggable: true,
    });
  };
  
  useEffect(() => {
    const auth = getAuth(app);
    onAuthStateChanged(auth, (user) => {
      if (user) {
        setUsername(user.displayName || user.email.split('@')[0]);
      } else {
        setUsername("Guest");
      }
    });
  }, []);

// Scroll to cart section
const scrollToCart = () => {
  cartRef.current.scrollIntoView({ behavior: "smooth" });
};

// Scroll visibility logic
useEffect(() => {
  if (cart.length > 0) {
    setIsButtonVisible(true);
  } else {
    setIsButtonVisible(false);
  }
}, [cart]);

// Intersection Observer to detect when cart section is in view
useEffect(() => {
  const observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        setIsButtonVisible(false);
      } else {
        setIsButtonVisible(true); 
      }
    },
    { threshold: 0.5 } 
  );

  if (cartRef.current) {
    observer.observe(cartRef.current);
  }

  return () => {
    if (cartRef.current) {
      observer.unobserve(cartRef.current);
    }
  };
}, []);

  return (
    <div className="container mt-5">
      <h2 className="mb-4 text-center">Point of Sale System</h2>

      {/* Button to scroll to cart */}
      {isButtonVisible && (
        <button
          className="btn btn-warning mt-3 fixed-bottom"
          onClick={scrollToCart}
          style={{ position: "fixed", bottom: "20px", left: "80%", transform: "translateX(-50%)", width:"100px" }}
        >
          <img src="images/cart.png" style={{width:"30px", height:"30px"}}></img>
        </button>
      )}
      
      {/* Search Bar */}
      <div className="mb-4">
        <input
          type="text"
          className="form-control"
          placeholder="Search for items..."
          value={searchQuery}
          onChange={handleSearch}
        />
      </div>
      <div className="mb-4" style={{ display: "flex", justifyContent: "flex-end" }}>
      <select
          className="form-select"
          value={selectedCategory}
          style={{width:"500px", marginLeft:"500"}}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          {categories.map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </select>
        <br></br>
        </div>

      <div className="row">
        {/* Inventory Section */}
        <div className="col-md-6">
          <div className="card-body">
            <div className="row">
              {filteredItems.length > 0 ? (
                filteredItems.map((item) => (
                  <div className="col-md-4 mb-3" key={item.itemId}>
                    <div className="card shadow-sm">
                      <div className="card-body">
                        <h6 className="card-title">{item.coffeeName}</h6>
                        {/* <p className="card-text">Available Quantity: {item.quantity}</p> */}
                        <p className="card-text">Quantity to add:</p>
                        <div className="d-flex justify-content-between">
                          <button
                            className="btn btn-outline-warning"
                            style={{height:"50px", width:"50px", marginRight:"10px"}}
                            onClick={() => handleSelectedQuantityChange(item.itemId, -1)}
                          >
                            -
                          </button>
                          <button
                            className="btn btn-outline-warning"
                            style={{height:"50px", width:"50px", marginRight:"10px"}}
                            onClick={() => handleSelectedQuantityChange(item.itemId, 1)}
                          >
                            +
                          </button>
                          <input
                            type="number"
                            className="form-control w-50"
                            value={selectedQuantity[item.itemId] || 1}
                            onChange={(e) => setSelectedQuantity((prev) => ({ ...prev, [item.itemId]: e.target.value }))}
                            min="1"
                          />
                        </div>
                        {Object.entries({ S: "small", M: "medium", L: "large" })
                        .filter(([sizeKey, sizeName]) => item.prices?.[sizeName] > 0)
                        .length > 0 && (
                          <>
                            <p className="card-text"><strong>Size</strong></p>
                            <div className="btn-group" role="group">
                              {Object.entries({ S: "small", M: "medium", L: "large" })
                                .filter(([sizeKey, sizeName]) => item.prices?.[sizeName] > 0)
                                .map(([sizeKey, sizeName]) => (
                                  <React.Fragment key={`${item.itemId}-${sizeKey}`}>
                                    <input
                                      type="radio"
                                      className="btn-check"
                                      name={`size-${item.itemId}`}
                                      id={`size-${item.itemId}-${sizeKey}`}
                                      autoComplete="off"
                                      checked={selectedSize[item.itemId] === sizeKey}
                                      onChange={() =>
                                        setSelectedSize((prev) => ({ ...prev, [item.itemId]: sizeKey }))
                                      }
                                    />
                                    <label
                                      className="btn btn-outline-primary"
                                      htmlFor={`size-${item.itemId}-${sizeKey}`}
                                    >
                                      {sizeKey}
                                    </label>
                                  </React.Fragment>
                                ))}
                            </div>
                          </>
                        )}
                      <button
                        className="btn btn-outline-primary w-100 mt-2"
                        onClick={() => addToCart(item)}
                        disabled={
                          item.quantity === 0 || 
                          (Object.entries({ S: "small", M: "medium", L: "large" })
                            .filter(([sizeKey, sizeName]) => item.prices?.[sizeName] > 0).length > 0 &&
                            !selectedSize[item.itemId])
                        }
                      >
                        Add to Cart
                      </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-12">
                  <p className="text-center text-muted">No items found matching your search.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Cart Section */}
        <div className="col-md-6">
          <div className="card mb-3" ref={cartRef}>
            <div className="card-header">
              <h5>Your Cart</h5>
            </div>
            <div className="card-body">
              {cart.length > 0 ? (
                <div>
                  <table className="table table-bordered">
                  <thead>
                      <tr>
                        <th style={{fontSize:"13px"}} >Item</th>
                        <th style={{fontSize:"13px"}}>Size</th>
                        <th style={{fontSize:"13px"}} >Price</th>
                        <th style={{fontSize:"13px"}} >Quantity</th>
                        <th style={{fontSize:"13px"}} >Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cart.map((item) => (
                        <tr key={`${item.itemId}-${item.size}`}>
                          <td style={{fontSize:"13px"}}>{item.coffeeName}</td>
                          <td style={{fontSize:"13px"}} >{item.size}</td>
                          <td style={{fontSize:"13px"}}>₱{item.price * item.quantity}</td>
                          <td>
                            <input
                              type="number"
                              value={item.quantity}
                              min="1"
                              style={{ width: "100%" }}
                              onChange={(e) => handleQuantityChangeInCart(e, item.itemId, item.size)}
                            />
                          </td>
                          <td>
                            <button
                              className="btn btn-outline-danger"
                              onClick={() => {
                                setCart(cart.filter((cartItem) => !(cartItem.itemId === item.itemId && cartItem.size === item.size)));
                              }}
                            >
                             <img src="images/delete.png" style={{width:"20px", height:"20px"}}></img>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <h5>Total: ₱{total.toFixed(2)}</h5>
                  {isButtonVisible && (
                    <button
                      className="btn btn-primary w-100 mt-3"
                      onClick={completeOrder}
                    >
                      Complete Order
                    </button>
                  )}
                </div>
              ) : (
                <p className="text-center text-muted">Your cart is empty.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default POS;
